import { useQuery } from '@tanstack/react-query';
import { FIRST_PAGE } from '@/api/pagination';
import type { FilterOption, SelectFilterField } from '@/components/common/AdvancedFilter';
import { formatDisplayDate } from '@/utils/date';
import { OPTION_DETAIL_SEPARATOR } from '@/utils/filters';
import { questionProjectsQueryOptions } from '../api/question.api';
import { PROJECT_LOOKUP_PAGE_SIZE } from '../constants/question.constants';
import type { QuestionProject } from '../types/question.types';

const ALL_PROJECTS = { page: FIRST_PAGE, pageSize: PROJECT_LOOKUP_PAGE_SIZE } as const;
const ACTIVE_SEARCH_TEXT = 'Active';
const INACTIVE_SEARCH_TEXT = 'Inactive';

function describeProject(project: QuestionProject): string {
  const declaredOn = project.declareDate ? formatDisplayDate(project.declareDate) : null;
  return [project.className, declaredOn].filter(Boolean).join(OPTION_DETAIL_SEPARATOR);
}

function toProjectOptions(projects: QuestionProject[] | undefined): FilterOption[] {
  return (projects ?? []).map((project) => {
    const details = describeProject(project);
    const statusText = project.isActive ? ACTIVE_SEARCH_TEXT : INACTIVE_SEARCH_TEXT;
    return {
      value: String(project.projectId),
      label: project.name,
      description: details,
      searchText: [details, statusText].join(OPTION_DETAIL_SEPARATOR),
      isActive: project.isActive,
    };
  });
}

export function useProjectFilterField(): SelectFilterField {
  const { data } = useQuery(questionProjectsQueryOptions(ALL_PROJECTS));

  return {
    key: 'projectId',
    label: 'Project',
    type: 'select',
    placeholder: 'Pick a project',
    options: toProjectOptions(data?.data),
    searchable: true,
    isRequired: true,
  };
}
